/* @flow */
import * as React from 'react';
import { debounce } from 'lodash';
import { graphql } from 'react-apollo';

import Button from 'components/Button';
import FormElement from 'components/FormElement';
import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import Input from 'components/Input';
import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import Text from 'components/Text';
import GET_TEACHERS_QUERY from './queries/GetTeachersQuery.gql';

import type { GetTeachers_organizations_teachers } from './queries/__generated__/GetTeachers';

import './styles/AdminTeacherTypeahead.scss';

const SEARCH_NETWORK_LIMIT_MS = 300; // for debounce
const MIN_QUERY_LENGTH = 2;

type Props = {
  organizationId: number,
  search: (q: string) => void,
  teachers: GetTeachers_organizations_teachers[],
  onSelect: (user: Object) => void,
  onDismissTeacher: () => void,
  onDirectAdd: (name: string) => void,
  loading: boolean,
};

type State = {
  query: string,
  teacherSelected: boolean,
  directAdd?: boolean,
  teacher?: ?GetTeachers_organizations_teachers,
};

class AdminTeacherTypeahead extends React.Component<Props, State> {
  static defaultProps: Props;
  state: State = {
    query: '',
    teacherSelected: false,
  };

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.organizationId !== nextProps.organizationId) {
      this.setState({ query: '', teacher: null, teacherSelected: false });
    }
  }

  isMinQueryLength = () => {
    return this.state && this.state.query.length > MIN_QUERY_LENGTH;
  };

  handleChange = (event: SyntheticInputEvent<>) => {
    event.stopPropagation();
    const query = event.target.value;
    this.setState({ query });
    this.performSearch(query);
  };

  handleClose = () => {
    const query = '';

    this.setState({ query, teacherSelected: false, teacher: null });

    this.performSearch(query);
    this.props.onDismissTeacher();
  };

  handleTeacherSelect = (user: Object) => {
    this.setState({ teacher: user, query: user.name, teacherSelected: true });

    this.props.onSelect(user);
  };

  performSearch = debounce((query: string) => {
    this.props.search(query);
  }, SEARCH_NETWORK_LIMIT_MS);

  renderResultList = (): * | Array<React.Element> => {
    const { teachers } = this.props;

    if (this.props.loading) {
      return (
        <div>
          <LoadingIndicator
            size="small"
            align="center"
            className="AdminTeacherTypeahead-results-item"
          />
        </div>
      );
    }

    if (teachers.length === 0) {
      return (
        <div
          data-test="teacher-typeahead-no-results"
          className="AdminTeacherTypeahead-results-item">
          <Heading levelStyle="6">No results</Heading>
          <Text size="xxsmall" color="eclipse">
            The person you’re searching for must have an account, be in your organization on Remind,
            and have a teacher role.
          </Text>
        </div>
      );
    }

    return teachers.map(teacher => this.renderResultListItem(teacher));
  };

  renderResultListItem(user: Object) {
    return (
      <Link
        block
        color="thunder"
        key={user.uuid}
        className="AdminTeacherTypeahead-results-item AdminTeacherTypeahead-results-item--link"
        onClick={() => this.handleTeacherSelect(user)}>
        {user.name}
      </Link>
    );
  }

  handleDirectAdd = () => this.props.onDirectAdd(this.state.query);

  render() {
    const closeButton = (
      <Button level="icon" aria-label="Clear selection" onClick={this.handleClose}>
        <Glyph name="close" size="small" solo />
      </Button>
    );

    return (
      <div className="AdminTeacherTypeahead-inputWrap">
        <FormElement
          data-test="teacher-typeahead-name-input"
          label="Full name"
          onValidChange={this.handleChange}
          element={Input}
          elementProps={{
            append: this.state.teacherSelected && closeButton,
            autocomplete: 'off',
            autofocus: true,
            glyph: this.state.teacherSelected ? null : 'search',
            id: 'AdminTeacherTypeahead-name',
            name: 'name',
            placeholder: 'Search for a teacher by first or last name',
            required: true,
            value: this.state.query,
          }}
        />
        {this.isMinQueryLength() &&
          !this.state.teacherSelected && (
            <div className="AdminTeacherTypeahead-dropdown">
              {this.renderResultList()}
              {this.state.teacher !== '' && (
                <Link
                  block
                  className="AdminTeacherTypeahead-results-item AdminTeacherTypeahead-results-item---link"
                  onClick={this.handleDirectAdd}>
                  <Text size="xxsmall">Add person</Text>
                </Link>
              )}
            </div>
          )}
      </div>
    );
  }
}

const withData = graphql(GET_TEACHERS_QUERY, {
  options: ({ organizationId }) => ({
    variables: { organizationId, query: '' },
    notifyOnNetworkStatusChange: true,
  }),
  props: ({ data }) => {
    return {
      loading: data.loading,
      teachers: data.organizations ? data.organizations[0].teachers : [],
      search(query: string) {
        data.refetch({
          query,
        });
      },
    };
  },
});

export { AdminTeacherTypeahead };
export default withData(AdminTeacherTypeahead);
